/* global google */
import { Controller } from "@hotwired/stimulus";
import { createCustomOverlay } from "./google_maps_popup";
import { populateSuggestions } from "./google_autocomplete";
import config from "@app/config";
export default class extends Controller {
  static targets = [
    "addressLookup",
    "latitude",
    "longitude",
    "state",
    "city",
    "firstline",
    "postcode",
    "suggestionsList",
    "toggleFallback",
    "toggleCancel",
    "addressFields",
  ];
  connect() {
    // Check if Google Maps API is loaded
    if (window.google && window.google.maps) {
      this.initMap();
      return;
    }

    // If script is not loading, start loading it
    if (!window.__googleMapsScriptLoading) {
      window.__googleMapsScriptLoading = true;
      this.loadMapScript(() => this.initMap());
      return;
    }

    // If script is loading, wait until it's loaded
    const checkLoaded = setInterval(() => {
      if (window.google && window.google.maps) {
        clearInterval(checkLoaded);
        this.initMap();
      }
    }, 100);
  }

  add_event_listeners() {
    if (this.hasToggleFallbackTarget && this.hasToggleCancelTarget) {
      if (!this.toggleFallbackTarget.__listenerAdded) {
        this.toggleFallbackTarget.addEventListener(
          "click",
          this.toggleAddressAutocomplete.bind(this)
        );
        this.toggleFallbackTarget.__listenerAdded = true;
      }
      if (!this.toggleCancelTarget.__listenerAdded) {
        this.toggleCancelTarget.addEventListener(
          "click",
          this.toggleAddressAutocomplete.bind(this)
        );
        this.toggleCancelTarget.__listenerAdded = true;
      }
    }
  }

  toggleAddressAutocomplete() {
    this.addressLookupTarget.classList.toggle("hidden");
    this.addressLookupTarget.toggleAttribute(
      "required",
      !this.addressLookupTarget.classList.contains("hidden")
    );
    if (this.addressLookupTarget.classList.contains("hidden")) {
      this.addressLookupTarget.value = "";
      this.addressLookupTarget.setCustomValidity("");
      this.addressLookupTarget.dispatchEvent(new Event("input", { bubbles: true }));
    }
    this.toggleFallbackTarget.classList.toggle("hidden");
    this.addressFieldsTarget.classList.toggle("hidden");
    this.toggleCancelTarget.classList.toggle("hidden");

    if (!this.addressFieldsTarget.classList.contains("hidden")) {
      const fields = [this.firstlineTarget, this.cityTarget, this.stateTarget, this.postcodeTarget];
      if (fields.some(field => field.value)) {
        fields.forEach(field => field.dispatchEvent(new Event("blur", { bubbles: true })));
      }
    }
  }

  loadMapScript(callback) {
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleApiKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (typeof callback === "function") {
        callback();
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
    document.body.appendChild(script);
  }

  async initAutocompleteSuggestion() {
    const { AutocompleteSuggestion, AutocompleteSessionToken } = await google.maps.importLibrary(
      "places"
    );
    populateSuggestions(AutocompleteSuggestion, AutocompleteSessionToken, this);
  }

  async initMap() {
    this.add_event_listeners();
    const controllerConfig = this.element.dataset.config;
    const mapElement = document.querySelector("[data-config='map']");

    if (controllerConfig === "autocomplete") {
      this.initAutocompleteSuggestion();
    }

    if (controllerConfig === "map" || mapElement) {
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
      const markers = JSON.parse(mapElement.dataset.markers);
      this.geocodeAddress(markers, mapElement, AdvancedMarkerElement, PinElement);
    }
  }

  geocodeAddress(markers, mapElement, AdvancedMarkerElement, PinElement) {
    const geocodePromises = markers.map((marker) => {
      if (marker.lat && marker.lng) {
        return Promise.resolve({
          location: new google.maps.LatLng(marker.lat, marker.lng),
          contentId: marker.content_id,
        });
      }
      return null;
    });

    Promise.all(geocodePromises)
      .then((locations) => {
        const validLocations = locations.filter((loc) => loc);

        if (validLocations.length === 0) {
          console.warn("No valid marker locations found.");
          return;
        }

        const map = new google.maps.Map(mapElement, {
          zoom: 13,
          center: validLocations[0].location,
          zoomControl: true,
          mapId: config.googleMapID,
        });

        if (mapElement.dataset.style === "light") {
          map.setOptions({
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          });
        }

        validLocations.forEach((locationObj) => {
          const defaultPinStyle = {
            borderColor: "#035e61",
            glyphColor: "#02484b",
          };
          const pinColorSetting = mapElement.dataset.pinColor;
          let pinContent;
          if (mapElement.hasAttribute("data-pin-color") && PinElement) {
            const backgroundColor =
              pinColorSetting && pinColorSetting !== "auto"
                ? pinColorSetting
                : defaultPinStyle.background;
            const pin = new PinElement({
              background: backgroundColor,
              borderColor: defaultPinStyle.borderColor,
              glyphColor: defaultPinStyle.glyphColor,
            });
            pinContent = pin.element;
          }

          const marker = new AdvancedMarkerElement({
            map: map,
            position: locationObj.location,
            content: pinContent,
          });

          const content = locationObj.contentId
            ? document.getElementById(locationObj.contentId)
            : null;
          if (content) {
            createCustomOverlay(map, marker, content, mapElement);
          }
        });
        // update the zoom and center to fit minimum of 10 markers
        if (validLocations.length > 1) {
          const bounds = new google.maps.LatLngBounds();
          validLocations.slice(0, 10).forEach((loc) => bounds.extend(loc.location));
          map.fitBounds(bounds);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }
}
